import React from "react";
import { TextField, Tooltip, Box } from "@mui/material";
import { useFormikContext } from "formik";

export interface GenericNumberFieldProps {
  name: string;
  label: string;
  disabled?: boolean;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  tooltip?: string;
  required?: boolean;
  size?: "small" | "medium";
}

const GenericNumberField: React.FC<GenericNumberFieldProps> = ({
  name,
  label,
  disabled = false,
  min,
  max,
  step = 1,
  placeholder,
  tooltip,
  required = false,
  size = "small",
}) => {
  const { values, errors, touched, setFieldValue, setFieldTouched } = useFormikContext<any>();

  // Helper function to get nested values safely
  const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  // Helper function to safely convert error to string
  const safeErrorToString = (error: any): string => {
    if (typeof error === 'string') return error;
    if (typeof error === 'object' && error !== null) {
      return Object.values(error).join(', ');
    }
    return '';
  };

  const fieldValue = getNestedValue(values, name) ?? "";
  const fieldError = getNestedValue(errors, name);
  const fieldTouched = getNestedValue(touched, name);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Convert empty string to null for number fields
    setFieldValue(name, value === "" ? null : Number(value));
  };

  const handleBlur = () => {
    setFieldTouched(name, true);
  };

  const numberField = (
    <TextField
      fullWidth
      size={size}
      name={name}
      label={label}
      type="number"
      value={fieldValue === null ? "" : fieldValue}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
      placeholder={placeholder}
      required={required}
      inputProps={{ min, max, step }}
      error={fieldTouched && Boolean(fieldError)}
      helperText={fieldTouched && fieldError ? safeErrorToString(fieldError) : ""}
      sx={{ mb: 2 }}
    />
  );

  return tooltip ? (
    <Tooltip title={tooltip} placement="top">
      <Box>{numberField}</Box>
    </Tooltip>
  ) : (
    numberField
  );
};

export default GenericNumberField;
